/**
 * Toolbox ▸ SDMS Template Builder — API client (`/api/v1/sdms-template-builder`).
 *
 * A template is one legacy `SpreadSheetBuilder_TemplateInfo` row (+ its .xlsx) and
 * five child sets — exactly what SDMS Result Entry reads (see the controller).
 */
import { api } from '../../../api/client';
import type { SheetEdits } from './sheetEdits';

const B = '/api/v1/sdms-template-builder';
type Env<T> = { data: T };

export interface TemplateListItem {
  templateID: number;
  templateName: string | null;
  templateTypeName: string | null;
  model: number;
  isActive: boolean;
  isRetire: boolean;
  isSampling: boolean;
  tests: string | null;
  matrix: string | null;
  method: string | null;
  mappedCells: number;
  hasWorkbook: boolean;
  inUse: boolean;
  modifiedByName: string | null;
  modifiedDate: string | null;
}

export interface TemplateHeader {
  templateID: number;
  templateName: string;
  templateTypeId: number | null;
  templateTypeName?: string | null;
  dtSheetID: number;
  isActive: boolean;
  isRetire: boolean;
  isSampling: boolean;
  isSampleBased: boolean;
  /** true = Vertical, false = Landscape */
  orientation: boolean;
  calibrationLevelNo: number;
  /** 0 batch sample entry · 1 sheet per run · 2 calibration set · 3 field sample collection */
  model: number;
  source: string | null;
  sampleOrientedSummary: boolean;
  parameterOrientedSummary: boolean;
  traceability: string | null;
  hasWorkbook?: boolean;
  inUse?: boolean;
  enteredByName?: string | null;
  enteredDate?: string | null;
  modifiedByName?: string | null;
  modifiedDate?: string | null;
  /** user Oids behind the two names — the legacy General Info pickers' values */
  enteredById?: string | null;
  modifiedById?: string | null;
  /** SAVE input: store the four stamps as sent (legacy General Info) instead of stamping automatically */
  stampsEdited?: boolean;
  /** Mail Merge tab: 0 Single sheet · 1 Multiple sheets · 2 Multiple documents (null = nothing stored) */
  mailMergeMode?: number | null;
  /** Mail Merge tab: 0 Landscape · 1 Vertical (null = nothing stored) */
  documentOrientation?: number | null;
  /** READ-ONLY: SDMS Template Builder code version that last saved the template, YY.MM.#### (server-stamped) */
  sdmsVersion?: string | null;
  /** READ-ONLY: YY.MM.### — month of the last save + how many times the template has been saved (server-stamped) */
  templateVersion?: string | null;
}

export interface TemplateTest {
  uqID: number;
  testMethodID: string;
  testParameterID: string;
  testName: string | null;
  methodName: string | null;
  matrixName: string | null;
  parameterName: string | null;
  qcTypeName: string | null;
  parameter: string | null;
  supportingParameter: string | null;
}

export interface TemplateField {
  uqID: number;
  fieldID: number;
  fieldName: string | null;
  resultType: string | null;
  caption_EN: string | null;
  caption_CN: string | null;
  width: number;
  visible: boolean;
  sort: number;
  exportSample: string | null;
  exportBlank: string | null;
  exportSpike: string | null;
  exportStandard: string | null;
  matchByHeader: boolean;
  importField: string | null;
}

export interface TemplateParsing {
  uqID: number;
  /** 1-based worksheet */
  sheetID: number;
  fieldID: number;
  fieldName: string | null;
  resultType: string | null;
  position: string | null;
  /** the cell's formula — `=FIELD("NAME")` for a merge field, or a calculation; empty = entry cell */
  formula: string | null;
  format: string | null;
  continuous: boolean;
  read: boolean;
  write: boolean;
  caption: string | null;
  defaultValue: string | null;
  usersFromDepartmentChain: boolean;
}

export interface TemplateHeaderMap { uqID: number; sheetID: number; caption: string | null; position: string | null }
export interface TemplateDetailMap { uqID: number; sheetID: number; columnName: string | null; columnIndex: number }

export interface TemplateDetail {
  header: TemplateHeader;
  tests: TemplateTest[];
  fields: TemplateField[];
  parsing: TemplateParsing[];
  headers: TemplateHeaderMap[];
  details: TemplateDetailMap[];
}

export type BandName = 'HEADERRANGE' | 'DETAILRANGE' | 'CHEADER' | 'CDETAIL';
export interface BandChange { sheetIndex: number; name: BandName; range: string | null }

export interface Lookups {
  templateTypes: { id: number; name: string | null; isSampling: boolean }[];
  fields: FieldOption[];
  testMethods: TestMethodOption[];
}
export interface FieldOption {
  id: number; resultType: string | null; fieldName: string | null;
  synonymEN: string | null; dataType: string | null; description: string | null;
}
export interface TestMethodOption {
  id: string; testName: string | null; methodName: string | null; matrixName: string | null; parameterCount: number;
}
export interface TestParameterOption { testParameterID: string; parameterName: string | null; qcTypeName: string | null }

/** Data Transfer & Reporting tab (SpreadSheetBuilder_DataTransfer / _Reporting). */
export type OutputKind = 'transfer' | 'report';
export interface DataTransferItem { uqID: number; name: string | null; hasFile: boolean; hasYaml: boolean; modifiedDate: string | null }
export interface ReportItem {
  uqID: number; name: string | null; fileType: string | null; subReport: boolean;
  reportSource: string | null; hasFile: boolean;
  /** The Windows builder's (legacy CodeDOM) layout is in force — it opens as it is, converted in memory. */
  usesLegacyLayout: boolean; modifiedDate: string | null;
}
export interface TemplateOutputs { dataTransfers: DataTransferItem[]; reports: ReportItem[] }

/** A new (uqID 0) or changed data transfer, saved with the template. */
export interface DataTransferEdit {
  uqID: number; name: string; sheetEdits?: SheetEdits; yamlBase64?: string; clearYaml?: boolean;
  /** Header / Detail band changes (HEADERRANGE / DETAILRANGE defined names). */
  bands?: BandChange[];
}
/** A new (uqID 0) or changed report row (its DEV layout is saved by the report designer). */
export interface ReportEdit { uqID: number; name: string; fileType: string; subReport: boolean; reportSource: string | null }
export interface OutputsSave { transfers: DataTransferEdit[]; reports: ReportEdit[] }

/** Legacy grid choices. */
export const FILE_TYPES = ['DEV', 'DOC', 'XLS'] as const;
export const REPORT_SOURCES = ['RawData', 'Calibration'] as const;

export interface ParsedInstrumentFile { columns: string[]; rows: string[][]; totalRows: number; warnings: string[] }
export interface DesignerSession { token: string; idleSeconds: number; report: string }

export const sdmsTemplateApi = {
  list: async (): Promise<TemplateListItem[]> =>
    ((await api.get(B)).data as Env<TemplateListItem[]>).data,
  lookups: async (): Promise<Lookups> =>
    ((await api.get(`${B}/lookups`)).data as Env<Lookups>).data,
  testParameters: async (testMethodId: string): Promise<TestParameterOption[]> =>
    ((await api.get(`${B}/test-parameters/${encodeURIComponent(testMethodId)}`)).data as Env<TestParameterOption[]>).data,
  get: async (id: number): Promise<TemplateDetail> =>
    ((await api.get(`${B}/${id}`)).data as Env<TemplateDetail>).data,
  /** The template .xlsx, or null when it has none. */
  workbook: async (id: number): Promise<ArrayBuffer | null> => {
    try {
      const res = await api.get(`${B}/${id}/workbook`, { responseType: 'arraybuffer' });
      const buf = res.data as ArrayBuffer;
      return buf && buf.byteLength > 0 ? buf : null;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
      throw e;
    }
  },
  create: async (template: TemplateDetail, bands: BandChange[], workbookBase64: string | null, sheetEdits?: SheetEdits): Promise<number> =>
    ((await api.post(B, { template, bands, workbookBase64, sheetEdits })).data as Env<{ templateId: number }>).data.templateId,
  update: async (id: number, template: TemplateDetail, bands: BandChange[], workbookBase64: string | null, sheetEdits?: SheetEdits): Promise<void> => {
    await api.put(`${B}/${id}`, { template, bands, workbookBase64, sheetEdits });
  },
  copy: async (id: number, templateName: string): Promise<number> =>
    ((await api.post(`${B}/${id}/copy`, { templateName })).data as Env<{ templateId: number }>).data.templateId,
  remove: async (id: number): Promise<void> => { await api.delete(`${B}/${id}`); },
  outputs: async (id: number): Promise<TemplateOutputs> =>
    ((await api.get(`${B}/${id}/outputs`)).data as Env<TemplateOutputs>).data,
  /** A data transfer's .xlsx, or a report's DevExpress layout (.repx). */
  outputFile: async (id: number, kind: OutputKind, uqId: number): Promise<Blob> =>
    (await api.get(`${B}/${id}/outputs/${kind}/${uqId}/file`, { responseType: 'blob' })).data as Blob,
  removeOutput: async (id: number, kind: OutputKind, uqId: number): Promise<void> => {
    await api.delete(`${B}/${id}/outputs/${kind}/${uqId}`);
  },
  /** Stores added / edited rows (and a data transfer's sheet edits / YAML); returns the fresh lists. */
  saveOutputs: async (id: number, body: OutputsSave): Promise<TemplateOutputs> =>
    ((await api.put(`${B}/${id}/outputs`, body)).data as Env<TemplateOutputs>).data,
  /** A data transfer's YAML bytes, or null when none is uploaded. */
  transferYaml: async (id: number, uqId: number): Promise<ArrayBuffer | null> => {
    try {
      return (await api.get(`${B}/${id}/outputs/transfer/${uqId}/yaml`, { responseType: 'arraybuffer' })).data as ArrayBuffer;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
      throw e;
    }
  },
  blankWorkbook: async (): Promise<ArrayBuffer> =>
    (await api.get(`${B}/blank-workbook`, { responseType: 'arraybuffer' })).data as ArrayBuffer,
  yamlColumns: async (yamlBase64: string): Promise<string[]> =>
    ((await api.post(`${B}/yaml/columns`, { yamlBase64 })).data as Env<string[]>).data,
  yamlParse: async (yamlBase64: string, fileBase64: string, fileName: string): Promise<ParsedInstrumentFile> =>
    ((await api.post(`${B}/yaml/parse`, { yamlBase64, fileBase64, fileName })).data as Env<ParsedInstrumentFile>).data,
  /** Opens the embedded report designer on one DEV report (a session bound to that row). */
  reportDesignerSession: async (id: number, uqId: number): Promise<DesignerSession> =>
    ((await api.post(`${B}/${id}/outputs/report/${uqId}/designer-session`)).data as Env<DesignerSession>).data,
};

/** The API's error message, else `fallback`. */
export function errorText(e: unknown, fallback: string): string {
  const err = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error;
  return err?.message ?? fallback;
}

/** ArrayBuffer → base64 (chunked so a multi-MB workbook does not blow the call stack). */
export function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
